import {Col, Switch} from 'antd';
import React, {FormEvent, ReactNode} from 'react';
import {Field, FieldRenderProps, Form, FormSpy} from 'react-final-form';
import {WithTranslation, withTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import {AttributeI, AttrSlugType} from '~src/interfaces/attributes.types';
import {
  CompanyRespWithSettingAttributes,
  CompanySettingsAttrsI,
  defaultFeatures,
  Features,
} from '~src/interfaces/company.interface';
import {SegmentType} from '~src/interfaces/segment.interface';
import {ContentLocalesI, SystemLocalesI, TimezonesI} from '~src/interfaces/session.types';
import {getAttributeBySlug} from '~src/modules/attributes/reducers/attributes.reducer';
import {OnSubmitSave} from '~src/modules/companySettings/containers/CompanySettings';
import {StoreI} from '~src/rootReducer';
import {validatorMinNumber1, validatorRequired} from '~src/services/validators';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import SelectInput from '~src/shared/form/select/SelectInput';
import {
  ActionRow,
  ButtonSection,
  ButtonStyled,
  Footer,
  LeftSection,
  LinkSectionStyled,
  QRCodeContainerStyled,
  RightSection,
  SectionBody,
  SectionHeader,
  SubSection,
  TextCol,
} from '~src/shared/form/shared.sc';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import ImageDropZone from '~src/shared/imageUploader/ImageDropZone';
import CustomFormInput from '~src/shared/reactFinalForm/FormInput';
import {composeValidators} from '~src/shared/reactFinalForm/validators';
import {TileContainer} from '~src/styles/containers.sc';
import {FieldType} from '~src/types';

import {FormState} from 'final-form';
import * as SC
  from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/surveyInvite.sc';
import CopyableInput from '~src/outer_modules/shared/copyableInput';
import QRCodeGenerator from '~src/outer_modules/shared/qrCodeGenerator';
import {routes} from '~src/routingConfig';
import {ConfirmationModal} from '~src/shared/ConfirmationModal';
import SvgIcon from '~src/shared/SvgIcon';
import {getUserDetails} from '~src/store/session/actions';
import {CompanyTemplateError} from './companySettingsForm.sc';

interface CompanyAttrsI {
  timezoneAttribute: AttributeI;
  systemLanguageAttribute: AttributeI;
  contentLanguageAttribute: AttributeI;
  anonymityMinAnswersAttr: AttributeI;
  adminsCanViewManagerSurveys: AttributeI;
  aiRecommendationsAttribute: AttributeI;
  spidergraphByRespondentsAttribute: AttributeI;
  selfSignUpAttribute: AttributeI;
}
interface FormValuesI {
  name: string;
  attributes: CompanySettingsAttrsI;
}

interface StateProps extends CompanyAttrsI {
  company: CompanyRespWithSettingAttributes;
  features: Partial<Features>;
  initialValues: FormValuesI;
}

interface DispatchProps {
  refetchUserDetails: () => void;
}

interface OwnProps {
  timezones: TimezonesI;
  contentLocales: ContentLocalesI;
  systemLocales: SystemLocalesI;
  onFrmSubmit: OnSubmitSave;
  onChange?: (values: FormValuesI) => void;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

interface State {
  isConfirmationModalVisible: boolean;
  showInviteQRModal: boolean;
}

class CompanySettingsForm extends React.Component<Props, State> {
  static defaultProps = {
    timezones: {},
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isConfirmationModalVisible: false,
      showInviteQRModal: false,
    };
  }

  toggleConfirmationModal = () =>
    this.setState((prevState) => ({
      isConfirmationModalVisible: !prevState.isConfirmationModalVisible,
    }));

  toggleSignupInviteQRModal = () =>
    this.setState((prevState) => ({
      showInviteQRModal: !prevState.showInviteQRModal,
    }));

  handleConfirmation = () => {
    this.setState({
      isConfirmationModalVisible: false,
    });
  };

  renderSwitchField = (
    props: FieldRenderProps<boolean, HTMLElement>,
    options: { showConfirmation: boolean } = { showConfirmation: true },
  ): ReactNode => {
    const { meta, input } = props;

    return (
      <div style={ButtonStyled}>
        <Switch
          {...input}
          checked={input.value}
          onChange={(value) => {
            if (value && options?.showConfirmation) {
              this.toggleConfirmationModal();
            }
            input.onChange(value);
          }}
        />
        {meta.touched && meta.error && <span>{meta.error}</span>}
      </div>
    );
  };

  renderField = ({ autoFocus, input, label, meta: { touched, error }, labelKey }: FieldType): JSX.Element => (
    <CustomFormInput
      {...input}
      autoFocus={autoFocus}
      error={touched ? error : null}
      label={label}
      type={input.type || 'text'}
      labelKey={labelKey}
    />
  );

  renderSelectInput = ({
    input,
    list,
    label,
    labelKey,
    meta: { touched, error },
  }: FieldType & { list: TimezonesI }): ReactNode => {
    const optList = Object.keys(list).map((k) => ({ name: list[k], value: k }));
    return (
      <SelectInput
        {...input}
        t={this.props.t}
        label={label}
        optList={optList}
        defaultValue={input.value}
        error={touched ? error : null}
        labelKey={labelKey}
      />
    );
  };

  onChange = (state: FormState<FormValuesI>) => this.props.onChange && this.props.onChange(state.values);

  render() {
    const { t, company } = this.props;
    const selfSignupInviteLink = `${window.location.origin}${routes.selfSignup(company.uuid)}`;

    const mapValBeforeSubmit = (values: FormValuesI) => {
      const { attributes } = values;
      const mappedAttrs: CompanySettingsAttrsI = Object.keys(attributes).reduce(
        (acc: CompanySettingsAttrsI, key) => {
          // TODO: refactoring needed, check whether we need this condition here!
          if (typeof attributes[key] === 'object' && attributes[key] !== null) {
            acc[key] = attributes[key].value;
          } else {
            acc[key] = attributes[key];
          }
          return acc;
        },
        {},
      );
      const coreValues = this.props.initialValues.name !== values.name ? { name: values.name } : {};

      return this.props.onFrmSubmit(coreValues, mappedAttrs).then(() => {
        this.props.refetchUserDetails();
      });
    };

    return (
      <TileContainer>
        <Form
          onSubmit={mapValBeforeSubmit}
          initialValues={this.props.initialValues}
          enableReinitialize
          keepDirtyOnReinitialize
        >
          {({ handleSubmit, pristine, form, valid, submitting, submitFailed, values }) => {
            const state = form.getState();
            const checkForError = (): boolean => {
              if ((pristine && state.touched) || (!pristine && submitFailed)) {
                return !valid;
              }
              return false;
            };
            return (
              <form onSubmit={handleSubmit} noValidate>
                <FormSpy subscription={{ values: true }} onChange={this.onChange} />
                <ConfirmationModal
                  show={this.state.isConfirmationModalVisible}
                  modalBody={t('x.company.settings.recommendations.modal')}
                  onCancel={() => {
                    this.toggleConfirmationModal();
                    form.change(`attributes.${this.props.aiRecommendationsAttribute.uuid}` as any, false);
                  }}
                  onSubmit={this.handleConfirmation}
                  submitButtonText={t('x.company.settings.recommendations.enable')}
                />
                <TileHeader
                  title={values.name || company.name}
                  avatar={{ type: SegmentType.Company, url: company.imageUrl, name: company.name }}
                  border
                />
                <SectionHeader data-translation="x.company.section.profile">
                  {t('x.company.section.profile')}
                </SectionHeader>
                <SectionBody>
                  <SubSection>
                    <Col md={8}>
                      <Field
                        autoFocus
                        name="name"
                        label={t('x.company.company_name')}
                        type="text"
                        children={this.renderField}
                        validate={validatorRequired}
                        labelKey="x.company.company_name"
                      />
                    </Col>
                    <Col md={8}>
                      <Field
                        name={`attributes.${this.props.timezoneAttribute.uuid}`}
                        label={t('x.company.timezones')}
                        children={this.renderSelectInput}
                        list={this.props.timezones}
                        validate={validatorRequired}
                        labelKey="'x.company.timezones"
                      />
                    </Col>
                    <Col md={8}>
                      <ImageDropZone type="company" />
                    </Col>
                  </SubSection>
                  <SubSection>
                    <Col md={8}>
                      <Field
                        name={`attributes.${this.props.systemLanguageAttribute.uuid}`}
                        label={t('x.company.prefered_system_language')}
                        children={this.renderSelectInput}
                        list={this.props.systemLocales}
                        validate={validatorRequired}
                        labelKey="x.company.prefered_system_language"
                      />
                    </Col>
                    <Col md={8}>
                      <Field
                        name={`attributes.${this.props.contentLanguageAttribute.uuid}`}
                        label={t('x.company.prefered_content_language')}
                        children={this.renderSelectInput}
                        list={this.props.contentLocales}
                        validate={validatorRequired}
                        labelKey="x.company.prefered_content_language"
                      />
                    </Col>
                  </SubSection>
                </SectionBody>
                {this.props.features.survey && (
                  <>
                    <SectionHeader data-translation="x.company.settings.section.anonymity">
                      {t('x.company.settings.section.anonymity')}
                    </SectionHeader>
                    <SectionBody>
                      <SubSection>
                        <Col lg={16} sm={14}>
                          <TextCol $tilesBottomPadding="5px">
                            <span>{t('x.company.settings.anonymity_min_answers_txt')}</span>
                          </TextCol>
                        </Col>
                        <Col lg={8} sm={10}>
                          <Field
                            name={`attributes.${this.props.anonymityMinAnswersAttr.uuid}`}
                            type="number"
                            label={t('x.company.settings.anonymity_min_answers_label')}
                            children={this.renderField}
                            validate={composeValidators([validatorRequired, validatorMinNumber1])}
                            labelKey="x.company.settings.anonymity_min_answers_label"
                          />
                        </Col>
                      </SubSection>
                      <SubSection>
                        <Col lg={16} sm={14}>
                          <TextCol $tilesBottomPadding="5px">
                            <span>{t('x.company.settings.admins_can_view_manager_surveys')}</span>
                          </TextCol>
                        </Col>
                        <Col lg={8} sm={10}>
                          <Field
                            name={`attributes.${this.props.adminsCanViewManagerSurveys.uuid}`}
                            type="switch"
                          >
                            {(props) => this.renderSwitchField(props, {showConfirmation:false})}
                          </Field>
                        </Col>
                      </SubSection>


                    </SectionBody>
                  </>
                )}
                {this.props.aiRecommendationsAttribute ? (
                  <SectionBody $tileSidePadding="0px" $tilesBottomPadding="0px">
                    <SectionHeader data-translation="x.company.settings.section.recommendations">
                      {t('x.company.settings.section.recommendations')}
                    </SectionHeader>
                    <SubSection>
                      <Col lg={16} sm={14}>
                        <TextCol $tileSidePadding="20px" $tilesBottomPadding="0px">
                          <span>{t('x.ai.settings.description_text')}</span>
                        </TextCol>
                      </Col>
                      <Col lg={8} sm={10}>
                        <Field
                          name={`attributes.${this.props.aiRecommendationsAttribute.uuid}`}
                          type={'switch'}
                        >
                          {(props) => this.renderSwitchField(props)}
                        </Field>
                      </Col>
                    </SubSection>
                  </SectionBody>
                ) : null}
                {this.props.spidergraphByRespondentsAttribute && (
                  <SectionBody $tileSidePadding="0px" $tilesBottomPadding="0px">
                    <SectionHeader data-translation="x.company.settings.section.spiderGraph.isAnswerByRespondentsEnabled">
                      {t('x.company.settings.section.spiderGraph.isAnswerByRespondentsEnabled')}
                    </SectionHeader>
                    <SubSection>
                      <Col lg={16} sm={14}>
                        <TextCol
                          $tileSidePadding="20px"
                          $tilesBottomPadding="0px"
                          data-translation="x.company.settings.section.spiderGraph.isAnswerByRespondentsEnabled.description"
                        >
                          <span>
                            {t(
                              'x.company.settings.section.spiderGraph.isAnswerByRespondentsEnabled.description',
                            )}
                          </span>
                        </TextCol>
                      </Col>
                      <Col lg={8} sm={10}>
                        <Field
                          name={`attributes.${this.props.spidergraphByRespondentsAttribute.uuid}`}
                          type={'switch'}
                        >
                          {(props) => this.renderSwitchField(props, { showConfirmation: false })}
                        </Field>
                      </Col>
                    </SubSection>
                  </SectionBody>
                )}
                {this.props.selfSignUpAttribute && (
                  <SectionBody $tileSidePadding="0px" $tilesBottomPadding="20px">
                    <SectionHeader data-translation="x.company.settings.section.isSelfSignUpAttributeEnabled">
                      {t('x.company.settings.section.isSelfSignUpAttributeEnabled')}
                    </SectionHeader>
                    <SubSection>
                      <LeftSection>
                        <TextCol
                          $tileSidePadding="20px"
                          $tilesBottomPadding="0px"
                          data-translation="x.company.settings.section.isSelfSignUpAttributeEnabled"
                        >
                          <span>
                            {t('x.company.settings.section.isSelfSignUpAttributeEnabled.description')}
                          </span>
                        </TextCol>
                      </LeftSection>
                      <RightSection>
                        <Field name={`attributes.${this.props.selfSignUpAttribute.uuid}`} type={'switch'}>
                          {(props) => this.renderSwitchField(props, { showConfirmation: false })}
                        </Field>
                      </RightSection>
                    </SubSection>
                    <SubSection>
                      <LeftSection>
                        <LinkSectionStyled
                          $hidden={Boolean(!values.attributes[this.props.selfSignUpAttribute.uuid])}
                        >
                          <CopyableInput value={selfSignupInviteLink} />
                        </LinkSectionStyled>
                      </LeftSection>
                      <RightSection>
                        <QRCodeContainerStyled
                          $hidden={Boolean(!values.attributes[this.props.selfSignUpAttribute.uuid])}
                        >
                          <SC.QRCodeLabelContainer
                            data-translation={'x.surveys.schedule.invite.qr.code.label'}
                            onClick={this.toggleSignupInviteQRModal}
                          >
                            <SC.QRCodeIcon>
                              <SvgIcon width={25} name="qrCode" />
                            </SC.QRCodeIcon>
                            <SC.QRCodeLabel>{t('x.surveys.schedule.invite.qr.code.label')}</SC.QRCodeLabel>
                          </SC.QRCodeLabelContainer>
                        </QRCodeContainerStyled>
                      </RightSection>
                      <SC.DialogStyled
                        show={this.state.showInviteQRModal}
                        onHide={this.toggleSignupInviteQRModal}
                        width={800}
                      >
                        <SC.DialogHeader data-translation="x.surveys.schedule.invite.qr.code.header">
                          {t('x.surveys.schedule.invite.qr.code.header')}
                        </SC.DialogHeader>
                        <QRCodeGenerator url={selfSignupInviteLink} />
                      </SC.DialogStyled>
                    </SubSection>
                  </SectionBody>
                )}

                {!pristine && (
                  <Footer>
                    <ActionRow>
                      <ButtonSection>
                        <SecondaryButton
                          onClick={(e: FormEvent) => {
                            e.preventDefault();
                            // FIXME: This seems like a bug in final form. When keepDirtyOnReinitialize is true,
                            // form.reset does not work
                            // form.reset(this.props.initialValues)
                            const { dirtyFields } = form.getState();

                            // FIXME: lodash.get should do exactly this, but for some reason it is not
                            // working with attributes.<uuid> values that we have
                            const getInitialFieldValue = (field: string) => {
                              if (!field.includes('.')) {
                                return this.props.initialValues[field as keyof FormValuesI];
                              }
                              let value: any = this.props.initialValues;
                              for (const key of field.split('.')) {
                                value = value[key as keyof FormValuesI];
                              }

                              return value;
                            };

                            Object.keys(dirtyFields).forEach((field) => {
                              const oldValue = getInitialFieldValue(field);
                              form.change(field as keyof FormValuesI, oldValue);
                            });
                          }}
                        >
                          <span data-translation="x.button.cancel">{t('x.button.cancel')}</span>
                        </SecondaryButton>
                        <PrimaryButtonLoading isLoading={submitting}>
                          <span data-translation="x.button.save">{t('x.button.save')}</span>
                        </PrimaryButtonLoading>
                      </ButtonSection>
                    </ActionRow>
                    {(checkForError() || state.error) && (
                      <CompanyTemplateError data-translation="x.surveys.schedules.template_error">
                        {t('x.surveys.schedules.template_error')}
                      </CompanyTemplateError>
                    )}
                  </Footer>
                )}
              </form>
            );
          }}
        </Form>
      </TileContainer>
    );
  }
}

const getAttributeValuesFromState = (state: StoreI, companyAttrs: CompanyAttrsI): CompanySettingsAttrsI => {
  const { setting_attributes } = state.user.session.company as CompanyRespWithSettingAttributes;
  return Object.values(companyAttrs).reduce((acc: CompanySettingsAttrsI, attr) => {
    if (attr && attr.uuid) {
      acc[attr.uuid] = setting_attributes[attr.uuid];
    }
    return acc;
  }, {});
};

const mapStateToProps = (state: StoreI): StateProps => {
  const companyAttrs: CompanyAttrsI = {
    timezoneAttribute: getAttributeBySlug(state, AttrSlugType.Timezone),
    systemLanguageAttribute: getAttributeBySlug(state, AttrSlugType.SystemLanguage),
    contentLanguageAttribute: getAttributeBySlug(state, AttrSlugType.ContentLanguage),
    anonymityMinAnswersAttr: getAttributeBySlug(state, AttrSlugType.AnonymityMinAnswers),
    aiRecommendationsAttribute: getAttributeBySlug(state, AttrSlugType.AIRecommendations),
    spidergraphByRespondentsAttribute: getAttributeBySlug(state, AttrSlugType.SpidergraphByRespondents),
    selfSignUpAttribute: getAttributeBySlug(state, AttrSlugType.SelfSignUp),
    adminsCanViewManagerSurveys: getAttributeBySlug(state, AttrSlugType.AdminsCanViewManagerSurveys)
  };
  return {
    ...companyAttrs,
    company: state.user.session.company!,
    features: (state.user.session.company && state.user.session.company.features) || defaultFeatures,
    initialValues: {
      name: state.user.session.company!.name,
      attributes: getAttributeValuesFromState(state, companyAttrs),
    },
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  refetchUserDetails: () => dispatch(getUserDetails()),
});

const CompanySettingsFormT = withTranslation()(CompanySettingsForm);
const CompanySettingsFormC = connect(mapStateToProps, mapDispatchToProps)(CompanySettingsFormT);

export default CompanySettingsFormC;
